import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'

import { blogCategoryPageModuleQuery } from '@data/sanity/queries/page'
import { SanityBlogCategory } from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getAllLocales, getLocaleLanguage, Locale } from '@lib/language'
import { useBlogCanonicalUrl } from '@lib/metadata'
import {
  BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import {
  CustomStaticPropsContext,
  PageType,
  StaticPathWithSlug,
} from '@lib/routes'
import {
  getAllBlogSettings,
  getBlogCategoryPage,
  getBlogLocale,
} from '@lib/sanity/blog'
import { getSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAllSlugs } from '@lib/sanity/page'

import Layout from '@components/layout'
import Modules from '@modules/modules'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type BlogCategoryPageProps = BasePageProps & {
  blogLocale: Locale
  slug: string
  page: SanityBlogCategory
  site: SanitySiteFragment
}

const BlogCategoryPage = ({
  draftMode,
  locale,
  blogLocale,
  slug,
  page,
  site,
}: BlogCategoryPageProps) => {
  const canonicalUrl = useBlogCanonicalUrl(
    PageType.BLOG_CATEGORY,
    page.slug,
    site.blogSettings,
  )

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      draftMode={draftMode}
      className="bg-gray text-black"
    >
      {!draftMode && <Modules posts={page.posts} />}
      {draftMode && (
        <PreviewModules
          posts={page.posts}
          query={blogCategoryPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
            blogCategorySlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface BlogCategoryPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<BlogCategoryPageParams>,
): Promise<GetStaticPropsResult<BlogCategoryPageProps>> {
  const locale = getLocaleLanguage(context.locale)

  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const blogLocale = await getBlogLocale(sanityClient, locale)
  const slug = context.params?.slug ?? ''
  const blogCategoryPage = await getBlogCategoryPage(
    sanityClient,
    locale,
    blogLocale,
    slug,
  )

  if (!blogCategoryPage.page) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      slug,
      page: blogCategoryPage.page,
      site: blogCategoryPage.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogCategoryPageParams>
> {
  const sanityClient = getSanityClient()
  const blogCategories = await getAllSlugs(sanityClient, 'blogCategory')
  const siteLocales = getAllLocales()
  const blogSettings = await getAllBlogSettings(sanityClient, siteLocales)
  const paths: StaticPathWithSlug[] = []

  blogCategories.forEach((blogCategory) => {
    const locale = blogCategory.locale as Locale

    if (blogSettings.hasLocaleContent[locale]) {
      // Add page locale path
      paths.push({
        params: {
          slug: blogCategory.slug,
        },
        locale,
      })

      // Add inherited locale paths
      for (const childLocale of blogSettings.childLocales[locale]) {
        paths.push({
          params: {
            slug: blogCategory.slug,
          },
          locale: childLocale,
        })
      }
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default BlogCategoryPage
